/**
 * Moxfield CSV exporter
 */
import { LANGUAGE_LABELS } from '@/lib/cardUtils';
import { generateCSV } from '@/lib/csv';
import type { EnrichedCollectionCard } from '@/types/card';

interface MoxfieldExportRow {
  [key: string]: string | number | boolean;
  Count: number;
  Name: string;
  Edition: string;
  Condition: string;
  Language: string;
  Foil: string;
  'Collector Number': string;
  Alter: string;
  'Playtest Card': string;
  'Purchase Price': string;
}

/**
 * Map condition code to Moxfield format
 */
function mapCondition(condition: string): string {
  const map: Record<string, string> = {
    M: 'Mint',
    NM: 'Near Mint',
    LP: 'Lightly Played',
    MP: 'Played',
    HP: 'Heavily Played',
    D: 'Damaged',
  };
  return map[condition] ?? 'Near Mint';
}

/**
 * Map finish to Moxfield foil field
 */
function mapFoil(finish: string): string {
  if (finish === 'foil') return 'foil';
  if (finish === 'etched') return 'etched';
  return '';
}

/**
 * Export to Moxfield CSV format
 */
export function exportMoxfield(cards: EnrichedCollectionCard[]): string {
  const rows: MoxfieldExportRow[] = cards.map((item) => ({
    Count: item.quantity,
    Name: item.card.name,
    Edition: item.card.set.toLowerCase(),
    Condition: mapCondition(item.condition),
    Language: LANGUAGE_LABELS[item.language] ?? 'English',
    Foil: mapFoil(item.finish),
    'Collector Number': item.card.collector_number,
    Alter: item.is_altered ? 'TRUE' : '',
    'Playtest Card': item.is_playtest ? 'TRUE' : 'FALSE',
    'Purchase Price': item.purchase_price?.toFixed(2) ?? '',
  }));

  const columns: { key: keyof MoxfieldExportRow; header: string }[] = [
    { key: 'Count', header: 'Count' },
    { key: 'Name', header: 'Name' },
    { key: 'Edition', header: 'Edition' },
    { key: 'Condition', header: 'Condition' },
    { key: 'Language', header: 'Language' },
    { key: 'Foil', header: 'Foil' },
    { key: 'Collector Number', header: 'Collector Number' },
    { key: 'Alter', header: 'Alter' },
    { key: 'Playtest Card', header: 'Playtest Card' },
    { key: 'Purchase Price', header: 'Purchase Price' },
  ];

  return generateCSV(rows, columns);
}
